import { DestroyRef, Injectable, OnDestroy } from '@angular/core';
import { NgZone } from '@angular/core';
import { initializeApp } from 'firebase/app';
import { AlertController } from '@ionic/angular/standalone';
import { SwPush } from '@angular/service-worker';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Subscription } from 'rxjs';
// import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  Channel,
  FirebaseMessaging,
  GetTokenOptions,
  Importance,
  Notification, NotificationActionPerformedListener,
  Visibility,NotificationReceivedEvent
} from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { StorageService } from './storage.service';

const LOGTAG = '[FirebaseMessagingService]';
const NOTISKEY = '_nots_';
interface ILNoti { notification:any, data:any };

@Injectable({
  providedIn: 'root'
})
export class FirebaseMessagingService implements OnDestroy {
  public readonly importance = Importance;
  public readonly visibility = Visibility;

  public token = '';
  public deliveredNotifications: Notification[] = [];
  public channels: Channel[] = [];

  private _liveNotis = new BehaviorSubject<ILNoti[]>([]);
  liveNotis$ = this._liveNotis.asObservable();

  // public createChannelFormGroup = new UntypedFormGroup({
  //   description: new UntypedFormControl(''),
  //   id: new UntypedFormControl(''),
  //   importance: new UntypedFormControl(Importance.Default),
  //   lightColor: new UntypedFormControl(''),
  //   lights: new UntypedFormControl(false),
  //   name: new UntypedFormControl(''),
  //   sound: new UntypedFormControl(''),
  //   vibration: new UntypedFormControl(false),
  //   visibility: new UntypedFormControl(Visibility.Private),
  // });

  private _swpusSub?:PushSubscription;
  private _swclickSub?:Subscription;
  ngOnDestroy(): void {
    if (!!this._swpusSub) {
      this._swpusSub.unsubscribe();
      delete this._swpusSub;
    }
  }

  constructor(
    private readonly ngZone: NgZone,
    private alertCtl: AlertController,
    private swpush: SwPush,
    private storage: StorageService,
    private dref: DestroyRef
  ) {
    // this.storage.ready.then(
    //   ()=>this.storage.get(NOTISKEY)
    // ).then((notis:ILNoti[]|undefined)=>{
    //   if (!!notis && notis.length) this._liveNotis.next(notis);
    // });
  }

  public async initializeFirebase(): Promise<string> {

    if (!Capacitor.isNativePlatform()) {
      this.storage.remove(NOTISKEY);
      
      initializeApp(environment.firebase);

      navigator.serviceWorker.addEventListener('message', (event: any) => {
        console.log(`${LOGTAG} serviceWorker message`, { event });
        const { data, notification, messageType } = event.data
        switch (messageType) {
          case 'push-received':
            navigator.serviceWorker.ready.then(
              (registration)=>registration.showNotification(notification.title, {
                ...notification, icon: "https://app.test.previntegral.com/assets/icons/icon-48.png",
                data: {
                  onActionClick: {default: {operation: "navigateLastFocusedOrOpen", "url": data.destination}}
                }
              })
            ).catch(err=>console.error(LOGTAG, err));
            const notis = [{notification, data}].concat(this._liveNotis.value);
            //this.storage.set(NOTISKEY, notis);
            this._liveNotis.next(notis);
            console.log(`${LOGTAG} serviceWorker push received`, notification, data );
            break;
          case 'notification-clicked':
            console.log(`${LOGTAG} serviceWorker noti clicked`, notification, data);
        }        
      });
    }

    FirebaseMessaging.removeAllListeners().then(() => {
      FirebaseMessaging.addListener('tokenReceived', (event) => {
        console.log(`${LOGTAG} tokenReceived`, { event });
        this.ngZone.run(() => {
          this.token = event.token;
        });
      });
      FirebaseMessaging.addListener('notificationReceived', (event) => {
        console.log(`${LOGTAG} notificationReceived`, { event });
        // Afegeix al storage a notis rebudes que se sumaràn a les del 'tray'
        // nota: cal fer-ho també a plataformes no natives per separat
        const { notification } = event;
        const { data } = notification;
        const notis = [{notification, data}].concat(this._liveNotis.value);
        // this.storage.set(NOTISKEY, notis);
        this._liveNotis.next(notis);
      });
    });

    return this.requestPermissions().then(
      ()=>this.getToken()
    ).then(()=>Promise.resolve(this.token));
  }

  public async requestPermissions(): Promise<void> {
    const { receive } = await FirebaseMessaging.checkPermissions();
    const doPrompt = async ()=>{
      if (!Capacitor.isNativePlatform()) {
        if (!this._swpusSub) {
          this._swpusSub = await this.swpush.requestSubscription({
            serverPublicKey:environment.firebase.vapidKey
          });
          this._swclickSub = this.swpush.notificationClicks.pipe(
            takeUntilDestroyed(this.dref)
          ).subscribe({
            next: ({action, notification})=>{
              console.log(LOGTAG + ' noti action:', action, notification);
            }
          });
          console.log(LOGTAG, 'listening to service worker notification clicks')
        }
      } else {
        const perms = await FirebaseMessaging.requestPermissions();
        if (perms.receive!='granted') throw new Error('permission not granted')
      }
    }

    switch ( receive ) {
      case 'granted': 
        if (!Capacitor.isNativePlatform()) await doPrompt(); 
        break;
      case 'prompt': 
        await doPrompt(); break;
      case 'prompt-with-rationale':
        // TODO: message translations!
        const alert = await this.alertCtl.create({
          header:'Por favor otorgue a continuacion los permisos de notificación a Previnapp.',
          message:'Previnapp le puede notificar eventos de su interés, etc...',
          buttons:['Aceptar', {text:'Cancelar', role:'cancel'}]
        });
        alert.present();
        const retv = await alert.onDidDismiss();
        if (retv.role) throw new Error('user cancelled');
        else {
          await doPrompt();
        }
        break;
      default: // 'denied'
        throw new Error(receive);
    }
  }

  public async getToken(): Promise<void> {
    const options: GetTokenOptions = {
      vapidKey: environment.firebase.vapidKey,
    };
    const { token } = await FirebaseMessaging.getToken(options);
    this.token = token;
  }

  public async deleteToken(): Promise<void> {
    this._swclickSub?.unsubscribe();
    delete this._swclickSub;
    this._swpusSub?.unsubscribe();
    delete this._swpusSub;
    await FirebaseMessaging.deleteToken();
    this.token = '';
  }

  // Nota: Cal inicialitzar l'acció de les notis en el constructor de l'app.component per poder modificar la ruta
  // en clicar una noti en fred (amb l'app nativa apagada). Pel cas webApp, se'n encarrega el service-worker.
  public registerAppNotiActionHandler(handler: NotificationActionPerformedListener) {
    if (Capacitor.isNativePlatform()) FirebaseMessaging.addListener('notificationActionPerformed', handler);
  }

  // public async createChannel(): Promise<void> {
  //   const description =
  //     this.createChannelFormGroup.get('description')?.value || '';
  //   const id = this.createChannelFormGroup.get('id')?.value || '';
  //   const importance =
  //     this.createChannelFormGroup.get('importance')?.value ||
  //     Importance.Default;
  //   const lightColor =
  //     this.createChannelFormGroup.get('lightColor')?.value || '';
  //   const lights = this.createChannelFormGroup.get('lights')?.value || false;
  //   const name = this.createChannelFormGroup.get('name')?.value || '';
  //   const sound = this.createChannelFormGroup.get('sound')?.value || '';
  //   const vibration =
  //     this.createChannelFormGroup.get('vibration')?.value || false;
  //   const visibility =
  //     this.createChannelFormGroup.get('visibility')?.value ||
  //     Visibility.Private;

  //   await FirebaseMessaging.createChannel({
  //     description,
  //     id,
  //     importance,
  //     lightColor,
  //     lights,
  //     name,
  //     sound,
  //     vibration,
  //     visibility,
  //   });
  //   await this.listChannels();
  // }

  // public async deleteChannel(channel: Channel): Promise<void> {
  //   await FirebaseMessaging.deleteChannel({
  //     id: channel.id,
  //   });
  //   await this.listChannels();
  // }

  public async listChannels(): Promise<void> {
    const { channels } = await FirebaseMessaging.listChannels();
    this.channels = channels;
  }

  public async getDeliveredNotifications(): Promise<void> {
    const result = await FirebaseMessaging.getDeliveredNotifications();
    this.deliveredNotifications = result.notifications;
  }

  public async removeAllDeliveredNotifications(): Promise<void> {
    await FirebaseMessaging.removeAllDeliveredNotifications();
    await this.getDeliveredNotifications();
  }

  public async removeDeliveredNotifications(
    notification: Notification,
  ): Promise<void> {
    await FirebaseMessaging.removeDeliveredNotifications({
      notifications: [notification],
    });
    await this.getDeliveredNotifications();
  }

  async discardLiveNoti(index:number) {
    if (index<0) throw new Error('invalid index: '+index);
    await this.storage.ready;
    const notis = await this.storage.get(NOTISKEY);
    console.log('stored livenotis', notis);
    if (!!notis && notis.length>index) {
        const [cut] = notis.splice(index, 1);
        this._liveNotis.next(notis.filter(()=>true));
        await this.storage.set(NOTISKEY, notis);
    } else throw new Error('invalid index: '+index);
  }
}